import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import Container from '../../layouts/Container';
import { useUsers } from '../../context/UserContext';
import {
  getResultsFormatCounts,
  getResultsFormats,
} from '../../services/project/resultsFormatService';

const getPayload = (response) => response?.data || response;
const getTodayIso = () => moment().format('YYYY-MM-DD');

const formatPercent = (count = 0, total = 0) => {
  if (!total) return '0%';
  return `${Math.round((Number(count) / Number(total)) * 100)}%`;
};

const normalizeRuleCounts = (list) => {
  if (Array.isArray(list)) {
    return list.map((item, index) => ({
      key: item.id || `${item.label || 'row'}-${index}`,
      label: item.label || item.ruleLabel || item.name || `Rule ${index + 1}`,
      count: Number(item.count ?? item.value ?? item.total ?? 0),
      fieldId: item.fieldId ?? null,
      fieldValue: item.fieldValue ?? null,
    }));
  }

  if (list && typeof list === 'object') {
    return Object.entries(list).map(([label, count], index) => ({
      key: `${label}-${index}`,
      label,
      count: Number(count ?? 0),
      fieldId: null,
      fieldValue: null,
    }));
  }

  return [];
};

const normalizeDetailedRegions = (responsePayload) => {
  const payload = getPayload(responsePayload);
  const regions = payload?.detailed?.regions;

  if (!Array.isArray(regions)) return [];

  return regions.map((region, regionIndex) => {
    const regionId = region?.regionId ?? `region-${regionIndex}`;

    return {
      key: String(regionId),
      regionId,
      regionName: (region?.regionName || '').trim() || 'Unassigned Region',
      totalEngagements: Number(region?.totalEngagements ?? 0),
      counts: normalizeRuleCounts(region?.counts || []),
      areas: Array.isArray(region?.areas)
        ? region.areas.map((area, areaIndex) => ({
            key: `${regionId}-${area?.areaId ?? areaIndex}`,
            areaId: area?.areaId ?? areaIndex,
            areaName: (area?.areaName || '').trim() || 'Unassigned Area',
            totalEngagements: Number(area?.totalEngagements ?? 0),
            counts: normalizeRuleCounts(area?.counts || []),
            users: Array.isArray(area?.users)
              ? area.users.map((entry, userIndex) => {
                  const user = entry?.user || {};
                  const name =
                    user.name ||
                    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                    user.username ||
                    `User ${userIndex + 1}`;

                  return {
                    key: `${regionId}-${area?.areaId ?? areaIndex}-${user.username || user.id || userIndex}`,
                    user: {
                      id: user.id ?? null,
                      username: user.username || '',
                      name,
                      aimsId: user.aimsId || null,
                    },
                    totalEngagements: Number(entry?.totalEngagements ?? 0),
                    counts: normalizeRuleCounts(entry?.counts || []),
                  };
                })
              : [],
          }))
        : [],
    };
  });
};

const getRuleValueText = (fieldValue) => {
  if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
    return 'Any value';
  }

  return String(fieldValue);
};

const toIdString = (value) => String(value);

const getCountForLabel = (counts, label) => {
  const row = (counts || []).find((item) => item.label === label);
  return Number(row?.count ?? 0);
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderColor: '#ced4da',
    boxShadow: 'none',
  }),
};

const ResultPage = () => {
  const { projectId } = useParams();
  const { isSuperAdmin } = useUsers();

  const [formats, setFormats] = useState([]);
  const [activeFormatId, setActiveFormatId] = useState('');

  const [startDate, setStartDate] = useState(getTodayIso());
  const [endDate, setEndDate] = useState(getTodayIso());

  const [loadingFormats, setLoadingFormats] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState([]);
  const [detailedRegions, setDetailedRegions] = useState([]);
  const [masterRegions, setMasterRegions] = useState([]);
  const [overallTotalEngagements, setOverallTotalEngagements] = useState(0);

  const [selectedRegionIds, setSelectedRegionIds] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);

  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedAreas, setExpandedAreas] = useState({});

  const activeFormat = useMemo(
    () => formats.find((format) => String(format.id) === String(activeFormatId)),
    [formats, activeFormatId]
  );

  const optionSourceRegions = useMemo(
    () => (masterRegions.length > 0 ? masterRegions : detailedRegions),
    [masterRegions, detailedRegions]
  );

  const regionOptions = useMemo(
    () =>
      optionSourceRegions.map((region) => ({
        label: region.regionName,
        value: toIdString(region.regionId),
      })),
    [optionSourceRegions]
  );

  const areaOptions = useMemo(() => {
    const sourceRegions = optionSourceRegions;

    if (selectedRegionIds.length > 0) {
      const selectedRegions = sourceRegions.filter((region) =>
        selectedRegionIds.includes(toIdString(region.regionId))
      );

      return selectedRegions.flatMap((region) =>
        (region.areas || []).map((area) => ({
          label: `${area.areaName} (${region.regionName})`,
          value: toIdString(area.areaId),
        }))
      );
    }

    return sourceRegions.flatMap((region) =>
      (region.areas || []).map((area) => ({
        label: `${area.areaName} (${region.regionName})`,
        value: toIdString(area.areaId),
      }))
    );
  }, [optionSourceRegions, selectedRegionIds]);

  const selectedRegionOptions = useMemo(
    () => regionOptions.filter((option) => selectedRegionIds.includes(option.value)),
    [regionOptions, selectedRegionIds]
  );

  const selectedAreaOptions = useMemo(
    () => areaOptions.filter((option) => selectedAreaIds.includes(option.value)),
    [areaOptions, selectedAreaIds]
  );

  const filteredRegions = useMemo(() => {
    return detailedRegions
      .filter(
        (region) =>
          selectedRegionIds.length === 0 ||
          selectedRegionIds.includes(toIdString(region.regionId))
      )
      .map((region) => {
        const filteredAreas = (region.areas || []).filter(
          (area) =>
            selectedAreaIds.length === 0 ||
            selectedAreaIds.includes(toIdString(area.areaId))
        );

        return {
          ...region,
          areas: filteredAreas,
        };
      })
      .filter((region) => selectedAreaIds.length === 0 || region.areas.length > 0);
  }, [detailedRegions, selectedAreaIds, selectedRegionIds]);

  const loadFormats = async () => {
    setLoadingFormats(true);
    setError(null);

    try {
      const formatsRes = await getResultsFormats(projectId);
      const formatsPayload = getPayload(formatsRes);
      const fetchedFormats =
        formatsPayload?.resultsFormats || formatsPayload?.data || formatsPayload || [];

      setFormats(fetchedFormats);

      const active = fetchedFormats.find((format) => format.isActive);
      setActiveFormatId(active ? String(active.id) : '');
    } catch (err) {
      console.error('Error loading result formats:', err);
      setError(err.response?.data?.errorMessage || 'Failed to load result formats.');
      setFormats([]);
      setRows([]);
      setDetailedRegions([]);
      setMasterRegions([]);
      setOverallTotalEngagements(0);
      setActiveFormatId('');
    } finally {
      setLoadingFormats(false);
    }
  };

  const loadCounts = async () => {
    if (!activeFormatId) {
      setRows([]);
      setDetailedRegions([]);
      setMasterRegions([]);
      setOverallTotalEngagements(0);
      return;
    }

    setLoadingCounts(true);
    setError(null);

    try {
      const res = await getResultsFormatCounts(projectId, activeFormatId, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        regionIds: selectedRegionIds,
        areaIds: selectedAreaIds,
      });

      const payload = getPayload(res);

      const normalizedRows = normalizeRuleCounts(
        payload?.total?.counts ||
          payload?.counts ||
          payload?.rows ||
          payload?.results ||
          payload?.data?.counts ||
          payload?.data ||
          payload
      );
      const normalizedDetailedRegions = normalizeDetailedRegions(res);

      setRows(normalizedRows);
      setDetailedRegions(normalizedDetailedRegions);
      setOverallTotalEngagements(
        Number(payload?.total?.totalEngagements ?? payload?.totalEngagements ?? 0)
      );

      if (selectedRegionIds.length === 0 && selectedAreaIds.length === 0) {
        setMasterRegions(normalizedDetailedRegions);
      }

      const nextExpandedRegions = {};
      const nextExpandedAreas = {};

      normalizedDetailedRegions.forEach((region) => {
        nextExpandedRegions[region.regionId] = false;

        (region.areas || []).forEach((area) => {
          nextExpandedAreas[`${region.regionId}-${area.areaId}`] = false;
        });
      });

      setExpandedRegions(nextExpandedRegions);
      setExpandedAreas(nextExpandedAreas);
    } catch (err) {
      console.error('Error loading result counts:', err);
      setError(err.response?.data?.errorMessage || 'Failed to load result counts.');
      setRows([]);
      setDetailedRegions([]);
      setOverallTotalEngagements(0);
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    loadFormats();
  }, [projectId]);

  useEffect(() => {
    if (activeFormatId) {
      loadCounts();
    }
  }, [activeFormatId]);

  useEffect(() => {
    if (selectedAreaIds.length === 0) return;

    const allowedAreaIdSet = new Set(areaOptions.map((area) => area.value));
    const nextSelectedAreaIds = selectedAreaIds.filter((id) => allowedAreaIdSet.has(id));

    if (nextSelectedAreaIds.length !== selectedAreaIds.length) {
      setSelectedAreaIds(nextSelectedAreaIds);
    }
  }, [areaOptions, selectedAreaIds]);

  const toggleRegion = (regionId) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionId]: !prev[regionId],
    }));
  };

  const toggleArea = (regionId, areaId) => {
    const key = `${regionId}-${areaId}`;

    setExpandedAreas((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loadCounts();
  };

  const handleClearFilters = () => {
    setStartDate(getTodayIso());
    setEndDate(getTodayIso());
    setSelectedRegionIds([]);
    setSelectedAreaIds([]);
  };

  const renderCountsTable = (counts, totalEngagements) => {
    if (!Array.isArray(counts) || counts.length === 0) {
      return (
        <Card className="mb-3">
          <Card.Body className="py-3 text-muted">No results counts available.</Card.Body>
        </Card>
      );
    }

    return (
      <>
        <Table striped bordered responsive className="mb-2">
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Label</th>
              <th className="text-end" style={{ width: 120 }}>Count</th>
              <th className="text-end" style={{ width: 140 }}>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {counts.map((item) => (
              <tr key={item.key}>
                <td>
                  <div className="fw-semibold">{item.label}</div>
                </td>
                <td className="text-end fw-semibold">{item.count}</td>
                <td className="text-end">{formatPercent(item.count, totalEngagements)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="text-end text-muted small mb-2">
          Total Engagements: <strong>{totalEngagements ?? 0}</strong>
        </div>
      </>
    );
  };

  const renderAreaUsersTable = (area) => {
    const users = area?.users || [];

    if (!users.length) {
      return <div className="text-muted small">No users found for this area.</div>;
    }

    const labelColumns = [
      ...new Set(
        users.flatMap((entry) => (entry?.counts || []).map((countRow) => countRow.label))
      ),
    ];

    return (
      <Table striped bordered responsive className="mb-0">
        <thead>
          <tr>
            <th style={{ minWidth: 220 }}>User</th>
            {labelColumns.map((label) => (
              <th key={`${area.key}-user-col-${label}`} className="text-end" style={{ width: 120 }}>
                {label}
              </th>
            ))}
            <th className="text-end" style={{ width: 140 }}>Engagements</th>
          </tr>
        </thead>
        <tbody>
          {users.map((entry) => (
            <tr key={entry.key}>
              <td>
                <div className="fw-semibold">{entry.user.name || entry.user.username || 'Unknown User'}</div>
              </td>
              {labelColumns.map((label) => (
                <td key={`${entry.key}-${label}`} className="text-end">
                  {getCountForLabel(entry.counts, label)}
                </td>
              ))}
              <td className="text-end fw-semibold">{entry.totalEngagements}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Results</h2>

        {isSuperAdmin && (
          <Button as={Link} to={`/projects/${projectId}/result/formats`} variant="outline-primary">
            Manage Result Formats
          </Button>
        )}
      </div>

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3 align-items-end">
              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Regions</Form.Label>
                  <Select
                    isMulti
                    isSearchable
                    options={regionOptions}
                    value={selectedRegionOptions}
                    onChange={(selected) =>
                      setSelectedRegionIds(
                        (Array.isArray(selected) ? selected : []).map((option) => option.value)
                      )
                    }
                    placeholder="All regions"
                    menuPortalTarget={document.body}
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                  />
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Areas</Form.Label>
                  <Select
                    isMulti
                    isSearchable
                    options={areaOptions}
                    value={selectedAreaOptions}
                    onChange={(selected) =>
                      setSelectedAreaIds(
                        (Array.isArray(selected) ? selected : []).map((option) => option.value)
                      )
                    }
                    placeholder="All areas"
                    menuPortalTarget={document.body}
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="d-flex gap-2">
                <Button type="submit" variant="primary" disabled={loadingCounts || !activeFormatId}>
                  {loadingCounts ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Refreshing...
                    </>
                  ) : (
                    'Apply Filters'
                  )}
                </Button>
                <Button type="button" variant="outline-secondary" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loadingFormats ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : !activeFormatId ? (
        <Alert variant="info" className="mb-0">
          No active result format found.{' '}
          {isSuperAdmin
            ? 'Set one as active in Manage Result Formats.'
            : 'Ask a super admin to set an active result format.'}
        </Alert>
      ) : rows.length === 0 && detailedRegions.length === 0 && !loadingCounts ? (
        <Alert variant="info" className="mb-0">
          No counts found for the selected filters.
        </Alert>
      ) : (
        <>
          {loadingCounts && <div className="text-muted small mb-2">Refreshing results...</div>}

          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Summary</h5>
              </div>
              {renderCountsTable(rows, overallTotalEngagements)}
            </Card.Body>
          </Card>

          {detailedRegions.length > 0 && (
            <>
              <h5 className="mb-3">Detailed by Region and Area</h5>

              {filteredRegions.length > 0 ? (
                filteredRegions.map((region) => (
                  <Card key={region.key} className="mb-3">
                    <Card.Header
                      className="d-flex justify-content-between align-items-center"
                      onClick={() => toggleRegion(region.regionId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="fw-semibold">
                        {region.regionName} ({region.totalEngagements})
                      </span>
                      <span className="fw-semibold" aria-hidden="true">
                        {expandedRegions[region.regionId] ? '▲' : '▼'}
                      </span>
                    </Card.Header>

                    {expandedRegions[region.regionId] && (
                      <Card.Body>
                        <h6 className="mb-2">Region Totals</h6>
                        {renderCountsTable(region.counts, region.totalEngagements)}

                        {region.areas.length > 0 && (
                          <>
                            <h6 className="mb-2">Areas</h6>
                            {region.areas.map((area) => {
                              const areaKey = `${region.regionId}-${area.areaId}`;

                              return (
                                <Card key={area.key} className="mb-2">
                                  <Card.Header
                                    className="d-flex justify-content-between align-items-center bg-light"
                                    onClick={() => toggleArea(region.regionId, area.areaId)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <span className="fw-semibold">
                                      {area.areaName} ({area.totalEngagements})
                                    </span>
                                    <span className="fw-semibold" aria-hidden="true">
                                      {expandedAreas[areaKey] ? '▲' : '▼'}
                                    </span>
                                  </Card.Header>

                                  {expandedAreas[areaKey] && (
                                    <Card.Body>
                                      {renderCountsTable(area.counts, area.totalEngagements)}
                                      <h6 className="mb-2">Users</h6>
                                      {renderAreaUsersTable(area)}
                                    </Card.Body>
                                  )}
                                </Card>
                              );
                            })}
                          </>
                        )}
                      </Card.Body>
                    )}
                  </Card>
                ))
              ) : (
                <Card>
                  <Card.Body className="text-muted">
                    No detailed data available for the selected filters.
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default ResultPage;
